// src/features/datev/werteSchutz.ts
//
// Werteschutz in der Intranet-Oberfläche (XTAX GmbH & Co. KG, 12.10.2026).
//
// IBAN, SV-Nummer (deutsch und europäisch) und Steuer-ID erscheinen verdeckt.
// Für Mitarbeiter aus dem Spiegel liefert die Datenbank die Masken; den
// Klartext holt anzeigenAusSpiegel über die Netlify-Funktion werte-anzeigen,
// die jede Anzeige protokolliert. Für Daten, die gerade live von DATEV kommen
// (Lohnaustausch- und Lohnergebnisdatenservice), liegt der Wert ohnehin im
// Browser; dort wird er nur verdeckt dargestellt.
//
// Die Feldnamen entsprechen JSON_FELDER in den Zwillingsdateien
// werteVerschluesselung.js / verschluesselung.ts der Server.

import { netlifyFetch } from '../../shared/services/netlifyFetch';

export type SchutzFeld = 'iban' | 'sv_nummer' | 'sv_nummer_eu' | 'steuer_id';

/** DATEV-Feldnamen der geschützten Werte */
export const SCHUTZ_SCHLUESSEL = new Set([
  'iban',
  'social_security_number',
  'european_social_security_number',
  'tax_identification_number'
]);

/** Platzhalter für einen verdeckten Wert ohne bekannte Maske */
export const VERDECKT = '••••••••';

function istSchutzSchluessel(feld: string): boolean {
  return SCHUTZ_SCHLUESSEL.has(String(feld).split('.').pop()!.toLowerCase());
}

/**
 * Kopie beliebiger Daten, in der die Werte der geschützten Felder verdeckt
 * sind. Bereits verschlüsselte Werte (Format v1) gelten ebenfalls als verdeckt.
 */
export function verdecke<T>(wert: T): T {
  const gehe = (knoten: any, verdeckt: boolean): any => {
    if (knoten === null || knoten === undefined) return knoten;
    if (Array.isArray(knoten)) return knoten.map((x) => gehe(x, verdeckt));
    if (typeof knoten === 'object') {
      const aus: Record<string, any> = {};
      for (const [k, v] of Object.entries(knoten)) aus[k] = gehe(v, verdeckt || istSchutzSchluessel(k));
      return aus;
    }
    if (verdeckt || (typeof knoten === 'string' && knoten.startsWith('v1:'))) return VERDECKT;
    return knoten;
  };
  return gehe(wert, false);
}

/** Klartext eines Spiegel-Mitarbeiters über werte-anzeigen (mit Protokoll). */
export async function anzeigenAusSpiegel(id: string, feld: SchutzFeld): Promise<string> {
  const response = await netlifyFetch('https://intranet.xtax.de/.netlify/functions/werte-anzeigen', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, feld })
  });
  const daten = await response.json().catch(() => ({}));
  if (!response.ok || daten.success === false) {
    throw new Error(daten.error || `Fehler ${response.status}`);
  }
  return String(daten.wert);
}
